import { Suspense, lazy, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';

import { datadogLogs } from '@datadog/browser-logs';

import webAuth from './components/auth/AuthConfigManager';
import { DrawerContext } from './components/drawers/DrawerManager';
import AuthTokenCheck from './components/fragments/AuthTokenCheck';
import SageLoader from './components/fragments/SageLoader';
import {
    AUTH0_CALLBACK_PATH,
    AUTH_STALL_WARNING_MS,
    LOGIN_SUCCESS_REDIRECT_PATH,
    NATIVE_AUTH_CALLBACK_PATH,
    NATIVE_ULP_PENDING_KEY,
    SSO_DIRECT_LOGIN_PATH,
    UNRESOLVED_CALLBACK_GRACE_MS,
} from './constants/Auth0';
import './fontawesome';
import LoginPage, { LogoutDialogTypes } from './pages/LoginPage';
import { selectBearerToken, selectHasCheckedToken, setBearerToken } from './store/authSlice';
import fetchWithRetry from './utils/fetchWithRetry';
import { useSageAuth } from 'src/auth/useSageAuth';
import Snackbar from 'src/components/snackbars/Snackbar';
import {
    GetIdentityProviderConnectionsApiResponse,
    useLazyGetIdentityProviderConnectionsQuery,
} from 'src/store/sageIdentityProviderSyncApi';
import {
    AuthCallbackResolution,
    describeAuthCallbackUrl,
    getAuth0ErrorCode,
    isLowConfidenceLoginError,
    resolveAuthCallback,
} from 'src/utils/authCallbackUtils';
import { setSsoDirectLoginDomain } from 'src/utils/ssoUtils';

const SageApp = lazy(() => fetchWithRetry(() => import('./SageApp'), 'SageApp'));

// Full page load of `/`, like a stuck user's manual fix: off the callback path, AuthTokenCheck's
// silent login can restore the session. `replace` keeps the dead callback URL out of history.
const restartLoginFromRoot = () => window.location.replace('/');

const hasPendingNativeUlp = () => sessionStorage.getItem(NATIVE_ULP_PENDING_KEY) !== null;

/**
 * This is the main entry point for the application. It handles both the initial and on-going authentication of users
 */
function App() {
    const { isLoading: isLoadingAuth, isAuthenticated, login, error } = useSageAuth();
    const { closeDrawer } = useContext(DrawerContext);
    const dispatch = useDispatch();
    const { hash, pathname, search } = useLocation();
    const navigate = useNavigate();

    const hasCheckedToken = useSelector(selectHasCheckedToken);
    const bearerToken = useSelector(selectBearerToken);
    const [getIdentityProviderConnections] = useLazyGetIdentityProviderConnectionsQuery();

    const [isProcessingLogin, setIsProcessingLogin] = useState<boolean>(
        () =>
            pathname === AUTH0_CALLBACK_PATH ||
            pathname === NATIVE_AUTH_CALLBACK_PATH ||
            pathname === SSO_DIRECT_LOGIN_PATH ||
            hasPendingNativeUlp()
    );
    // Native auth reports the session before login leaves the page, re-running the effect below.
    const hasStartedIdpLogin = useRef(false);

    const isLoading = isLoadingAuth || isProcessingLogin || !hasCheckedToken;

    useEffect(() => {
        if (!isLoadingAuth && !isAuthenticated) {
            closeDrawer();
        }
    }, [closeDrawer, isAuthenticated, isLoadingAuth]);

    /**
     * This effect handles for the redirect back to this application after the user has authenticated
     * with Auth0, either via passwordless or ULP. In both cases, the user will be redirected to /auth_confirmed.
     *
     * - For passwordless, the URL hash contains user's auth details (token, idToken, expiresIn).
     *   We need to parse this hash ourselves.
     * - For ULP, Auth0 will redirect back with a "code" search param, which will be handled by the Auth0Provider.
     *
     * After the user is authenticated via one of the above methods, we can redirect the user to the user shift page.
     */
    useEffect(() => {
        const redirectToLoginWithError = (_message: string, logoutReason?: LogoutDialogTypes) => {
            setIsProcessingLogin(false);

            // TODO: this is currently being called outside of the provider and is erroring out. We should refactor the snackbar to be a properly configured React Context provider so that we can call this function here without issue.
            // snackActions.error(message);
            navigate(
                logoutReason ? `/authenticate?logoutReason=${logoutReason}` : '/authenticate',
                { replace: true }
            );
        };

        if (isLoadingAuth) {
            // Wait until Auth0 has finished loading
            return;
        }

        if (hasPendingNativeUlp()) {
            setIsProcessingLogin(false);
            // Kept through a failed session read, so the shell's retry still lands here.
            if (error) return;
            sessionStorage.removeItem(NATIVE_ULP_PENDING_KEY);
            if (isAuthenticated) navigate(LOGIN_SUCCESS_REDIRECT_PATH, { replace: true });
            return;
        }

        if (pathname === AUTH0_CALLBACK_PATH) {
            setIsProcessingLogin(true);
            // the useLocation search property seems to be stale here, so using the window.location.search instead
            const searchParams = new URLSearchParams(window.location.search);
            const resolution = resolveAuthCallback({ hash, isAuthenticated, searchParams, error });

            if (resolution === AuthCallbackResolution.ParseHash) {
                /**
                 * The `isAuthenticated` property defaults to `false`. The @auth0/auth0-react requires the specified redirect_uri to load completely
                 * before evaluating the user session and updating `isAuthenticated = true`, even if `isLoading === false`.
                 *
                 * See https://github.com/auth0/auth0-react/issues/82#issuecomment-711479502
                 */
                webAuth.parseHash({ hash }, (parseError, authResult) => {
                    if (parseError) {
                        datadogLogs.logger.warn(
                            'Error parsing hash:',
                            parseError,
                            new Error(
                                parseError?.errorDescription ??
                                    parseError?.error ??
                                    'Unknown Auth0 parse error'
                            )
                        );
                        redirectToLoginWithError(
                            'There was an error logging you in, please log in using a different method.',
                            isLowConfidenceLoginError(parseError.error, parseError.errorDescription)
                                ? LogoutDialogTypes.VerificationRequired
                                : undefined
                        );
                    } else if (!authResult || !authResult.accessToken) {
                        datadogLogs.logger.warn(
                            'Unable to parse accessToken from authResult',
                            undefined,
                            new Error('Missing accessToken')
                        );
                        redirectToLoginWithError(
                            'There was an error logging you in, please try again.'
                        );
                    } else {
                        const { accessToken } = authResult;
                        dispatch(setBearerToken(`Bearer ${accessToken}`));
                        setIsProcessingLogin(false);
                        navigate(LOGIN_SUCCESS_REDIRECT_PATH, { replace: true });
                    }
                });
            } else if (resolution === AuthCallbackResolution.IdpInitiatedLogin) {
                /**
                 * When the state param is not present, we are dealing with IdP-initiated login. To
                 * grab the user token, we need to initiate loginWithRedirect with the connection param
                 * which is configured to be passed into the callback url.
                 *
                 * When we reach this block, isAuthenticated may already be true, if the user had
                 * a previously valid session. Even though Auth0 has already established the
                 * IdP-initiated session, AuthTokenCheck calls getAccessTokenSilently before we hit
                 * this block, which checks the local cache for a valid session. If that session is
                 * for a different user, the auth0-react state will be inaccurate, so this forces
                 * a full refresh and fresh token exchange.
                 *
                 * See https://github.com/auth0/auth0-spa-js/issues/213
                 */
                if (!hasStartedIdpLogin.current) {
                    hasStartedIdpLogin.current = true;
                    // Native SSO rejects when the Custom Tab is dismissed; without this the
                    // loader never clears and hasStartedIdpLogin blocks a retry.
                    login({ connection: searchParams.get('connection') ?? '' }).catch(
                        (loginError) => {
                            datadogLogs.logger.warn(
                                'Unable to complete IdP-initiated login',
                                undefined,
                                loginError
                            );
                            redirectToLoginWithError(
                                'There was an error logging you in, please try again.'
                            );
                        }
                    );
                }
            } else if (resolution === AuthCallbackResolution.UrlError) {
                datadogLogs.logger.warn(
                    'Auth0 failed to authenticate the user',
                    {
                        urlError: searchParams.get('error'),
                        urlErrorDescription: searchParams.get('error_description'),
                    },
                    error
                );
                redirectToLoginWithError(`There was an error logging you in, please try again.`);
            } else if (resolution === AuthCallbackResolution.SdkError) {
                datadogLogs.logger.warn(
                    'Auth0 failed to handle the login redirect',
                    {
                        auth0Error: getAuth0ErrorCode(error),
                        ...describeAuthCallbackUrl(hash, searchParams),
                    },
                    error
                );
                restartLoginFromRoot();
            } else if (resolution === AuthCallbackResolution.Success) {
                setIsProcessingLogin(false);
                navigate(LOGIN_SUCCESS_REDIRECT_PATH, { replace: true });
            } else {
                const timeout = setTimeout(() => {
                    datadogLogs.logger.warn('Unable to resolve the login callback', {
                        isAuthenticated,
                        ...describeAuthCallbackUrl(hash, searchParams),
                    });
                    restartLoginFromRoot();
                }, UNRESOLVED_CALLBACK_GRACE_MS);

                return () => clearTimeout(timeout);
            }
        }

        if (pathname === NATIVE_AUTH_CALLBACK_PATH) {
            // The shell intercepts this redirect, so reaching it means the code was never redeemed.
            datadogLogs.logger.warn('Native auth callback reached the web app', {
                ...describeAuthCallbackUrl(hash, new URLSearchParams(window.location.search)),
            });
            restartLoginFromRoot();
            return;
        }

        if (pathname === SSO_DIRECT_LOGIN_PATH) {
            setIsProcessingLogin(true);
            const searchParams = new URLSearchParams(search);
            if (!searchParams.has('domain')) {
                redirectToLoginWithError('Must specify domain parameter in url.');
            } else if (isAuthenticated) {
                // avoids forcing a fresh login on a cold start if the user is already authenticated
                // sessionStorage will be empty on a cold launch, so re-persist the domain
                setSsoDirectLoginDomain(searchParams.get('domain') || '');
                setIsProcessingLogin(false);
                navigate(LOGIN_SUCCESS_REDIRECT_PATH, { replace: true });
            } else {
                const domain = searchParams.get('domain') || '';
                (async () => {
                    try {
                        const response: GetIdentityProviderConnectionsApiResponse =
                            await getIdentityProviderConnections({
                                domain,
                            }).unwrap();

                        // only set if successfully resolves to an Auth0 connection
                        setSsoDirectLoginDomain(domain);

                        // Awaited so a native rejection reaches the catch. Success needs no
                        // cleanup: both backends leave the page.
                        await login({
                            connection: response.auth0ConnectionName,
                            forceLogin: true,
                        });
                    } catch (error: any) {
                        datadogLogs.logger.warn(
                            'Unable to log in from direct sso',
                            undefined,
                            error
                        );

                        setSsoDirectLoginDomain(undefined);

                        redirectToLoginWithError(
                            'There was an error logging you in, please try again.'
                        );
                    }
                })();
            }
        }
    }, [
        dispatch,
        hash,
        isAuthenticated,
        isLoadingAuth,
        navigate,
        pathname,
        login,
        getIdentityProviderConnections,
        error,
        search,
    ]);

    // Visibility only: log when the auth loader makes no progress, on any page load
    useEffect(() => {
        if (!isLoading) {
            return;
        }

        const timeout = setTimeout(() => {
            datadogLogs.logger.warn('Auth loader stalled', {
                pathname,
                isLoadingAuth,
                isAuthenticated,
                isProcessingLogin,
                hasCheckedToken,
                auth0Error: getAuth0ErrorCode(error),
                auth0ErrorMessage: error?.message,
                ...describeAuthCallbackUrl(hash, new URLSearchParams(window.location.search)),
            });
        }, AUTH_STALL_WARNING_MS);

        return () => clearTimeout(timeout);
    }, [
        error,
        hash,
        hasCheckedToken,
        isAuthenticated,
        isLoading,
        isLoadingAuth,
        isProcessingLogin,
        pathname,
    ]);

    return (
        <>
            <Snackbar />
            {isLoading && (
                <SageLoader message="Verifying your information..." showReloadLink record />
            )}
            {!isLoading && (isAuthenticated || !!bearerToken) && (
                <Suspense fallback={<SageLoader showReloadLink record />}>
                    <SageApp />
                </Suspense>
            )}
            {!isLoading && !isAuthenticated && !bearerToken && (
                <Suspense fallback={<SageLoader showReloadLink record />}>
                    <Routes>
                        <Route path="/authenticate" element={<LoginPage />} />

                        <Route path="*" element={<Navigate to="/authenticate" replace />} />
                    </Routes>
                </Suspense>
            )}
            <AuthTokenCheck isProcessingLogin={isProcessingLogin} />
        </>
    );
}

export default App;
